import { useState, useEffect, useCallback, createContext, useContext } from 'react'
import {
  signIn,
  signOut,
  getCurrentUser,
  fetchAuthSession,
  fetchUserAttributes,
} from 'aws-amplify/auth'
import { Hub } from 'aws-amplify/utils'

// Auth context for sharing auth state across components
const AuthContext = createContext(null)

/**
 * Auth Provider component - wrap your app with this
 */
export function AuthProvider({ children }) {
  const auth = useProvideAuth()
  return <AuthContext.Provider value={auth}>{children}</AuthContext.Provider>
}

/**
 * Hook to access auth state and methods
 */
export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

/**
 * Internal hook that provides auth functionality
 */
function useProvideAuth() {
  const [user, setUser] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [accessToken, setAccessToken] = useState(null)
  const [idToken, setIdToken] = useState(null)
  const [error, setError] = useState(null)

  // Fetch current authenticated user
  const checkUser = useCallback(async () => {
    try {
      setIsLoading(true)
      setError(null)

      const currentUser = await getCurrentUser()
      const session = await fetchAuthSession()
      const attributes = await fetchUserAttributes()

      // Extract tokens
      const tokens = session.tokens
      const accessTokenStr = tokens?.accessToken?.toString()
      const idTokenStr = tokens?.idToken?.toString()

      // Build user object
      const userData = {
        username: currentUser.username,
        userId: currentUser.userId,
        email: attributes.email,
        name: attributes.name || attributes.email?.split('@')[0],
        givenName: attributes.given_name,
        familyName: attributes.family_name,
        attributes,
      }

      setUser(userData)
      setAccessToken(accessTokenStr)
      setIdToken(idTokenStr)
      setIsAuthenticated(true)

      console.log('User authenticated:', userData.email)
      return userData
    } catch (err) {
      // Not authenticated - this is normal for unauthenticated users
      console.log('No authenticated user')
      setUser(null)
      setAccessToken(null)
      setIdToken(null)
      setIsAuthenticated(false)
      return null
    } finally {
      setIsLoading(false)
    }
  }, [])

  // Sign in with redirect (hosted UI)
  const handleSignIn = useCallback(async (provider = 'COGNITO') => {
    try {
      setError(null)
      // This will redirect to Cognito hosted UI
      await signIn({
        provider: provider === 'COGNITO' ? undefined : provider,
      })
    } catch (err) {
      console.error('Sign in error:', err)
      setError(err.message || 'Failed to sign in')
      throw err
    }
  }, [])

  // Sign in with federated provider (e.g., SAML, Google)
  const handleFederatedSignIn = useCallback(async (provider) => {
    try {
      setError(null)
      await signIn({ provider })
    } catch (err) {
      console.error('Federated sign in error:', err)
      setError(err.message || 'Failed to sign in')
      throw err
    }
  }, [])

  // Sign out
  const handleSignOut = useCallback(async (global = false) => {
    try {
      setError(null)
      await signOut({ global })
      setUser(null)
      setAccessToken(null)
      setIdToken(null)
      setIsAuthenticated(false)
      console.log('User signed out')
    } catch (err) {
      console.error('Sign out error:', err)
      setError(err.message || 'Failed to sign out')
      throw err
    }
  }, [])

  // Get fresh access token (for API calls)
  const getAccessToken = useCallback(async () => {
    try {
      const session = await fetchAuthSession({ forceRefresh: false })
      const token = session.tokens?.accessToken?.toString()
      setAccessToken(token)
      return token
    } catch (err) {
      console.error('Error getting access token:', err)
      return null
    }
  }, [])

  // Get fresh ID token (contains user claims)
  const getIdToken = useCallback(async () => {
    try {
      const session = await fetchAuthSession({ forceRefresh: false })
      const token = session.tokens?.idToken?.toString()
      setIdToken(token)
      return token
    } catch (err) {
      console.error('Error getting ID token:', err)
      return null
    }
  }, [])

  // Listen for auth events
  useEffect(() => {
    // Check initial auth state
    checkUser()

    // Subscribe to auth events
    const unsubscribe = Hub.listen('auth', ({ payload }) => {
      console.log('Auth event:', payload.event)

      switch (payload.event) {
        case 'signedIn':
          console.log('User signed in')
          checkUser()
          break
        case 'signedOut':
          console.log('User signed out')
          setUser(null)
          setAccessToken(null)
          setIdToken(null)
          setIsAuthenticated(false)
          break
        case 'tokenRefresh':
          console.log('Token refreshed')
          getAccessToken()
          getIdToken()
          break
        case 'tokenRefresh_failure':
          console.error('Token refresh failed')
          setError('Session expired. Please sign in again.')
          break
        case 'signInWithRedirect':
          console.log('Sign in with redirect initiated')
          break
        case 'signInWithRedirect_failure':
          console.error('Sign in with redirect failed')
          setError('Sign in failed. Please try again.')
          break
        case 'customOAuthState':
          console.log('Custom OAuth state:', payload.data)
          break
        default:
          break
      }
    })

    return () => unsubscribe()
  }, [checkUser, getAccessToken, getIdToken])

  return {
    user,
    isLoading,
    isAuthenticated,
    accessToken,
    idToken,
    error,
    signIn: handleSignIn,
    signOut: handleSignOut,
    federatedSignIn: handleFederatedSignIn,
    getAccessToken,
    getIdToken,
    checkUser,
    clearError: () => setError(null),
  }
}

export default useAuth
